import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type Experience = {
  id: string;
  slug: string;
  label: string;
  tagline: string | null;
  display_order: number;
  is_default: boolean;
  include_in_picker: boolean;
};

export type SlideOverride = {
  slide_id: string;
  experience_id: string;
  kicker: string | null;
  title: string | null;
  caption: string | null;
  caption_long: string | null;
  media_url: string | null;
  media_type: string | null;
};

function anonClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

// ---------- Public reads ----------

export const listExperiences = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = anonClient();
  const { data, error } = await supabase
    .from("experiences")
    .select("id,slug,label,tagline,display_order,is_default,include_in_picker")
    .order("display_order", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as Experience[];
});

// ---------- Admin: experiences ----------

const addExpSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9-]+$/, "lowercase letters, digits, and dashes only"),
  label: z.string().min(1).max(100),
});

export const addExperience = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => addExpSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: maxRow } = await supabase
      .from("experiences")
      .select("display_order")
      .order("display_order", { ascending: false })
      .limit(1);
    const nextOrder = ((maxRow?.[0]?.display_order ?? -1) as number) + 1;
    const { data: inserted, error } = await supabase
      .from("experiences")
      .insert({
        slug: data.slug,
        label: data.label,
        display_order: nextOrder,
        is_default: false,
        include_in_picker: true,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);

    // Auto-link the new experience to every existing category and slide,
    // so admins don't have to tick every checkbox after creating a path.
    const { data: cats } = await supabase
      .from("slides")
      .select("category_id");
    const uniqueCats = Array.from(new Set((cats ?? []).map((c) => c.category_id)));
    if (uniqueCats.length > 0) {
      await supabase
        .from("category_experiences")
        .upsert(uniqueCats.map((cid) => ({ category_id: cid, experience_id: inserted!.id })));
    }
    const { data: allSlides } = await supabase.from("slides").select("id");
    if (allSlides && allSlides.length > 0) {
      await supabase
        .from("slide_experiences")
        .upsert(allSlides.map((s) => ({ slide_id: s.id, experience_id: inserted!.id })));
    }
    return { ok: true, id: inserted!.id };
  });

const updateExpSchema = z.object({
  id: z.string().uuid(),
  label: z.string().min(1).max(100).optional(),
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9-]+$/)
    .optional(),
  tagline: z.string().max(120).nullable().optional(),
  include_in_picker: z.boolean().optional(),
});

export const updateExperience = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => updateExpSchema.parse(input))
  .handler(async ({ data, context }) => {
    const patch: {
      updated_at: string;
      label?: string;
      slug?: string;
      tagline?: string | null;
      include_in_picker?: boolean;
    } = { updated_at: new Date().toISOString() };
    if (data.label !== undefined) patch.label = data.label;
    if (data.slug !== undefined) patch.slug = data.slug;
    if (data.tagline !== undefined) patch.tagline = data.tagline;
    if (data.include_in_picker !== undefined) patch.include_in_picker = data.include_in_picker;
    const { error } = await context.supabase
      .from("experiences")
      .update(patch)
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });


const deleteExpSchema = z.object({ id: z.string().uuid() });

export const deleteExperience = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => deleteExpSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("experiences").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const reorderExpSchema = z.object({
  ordered_ids: z.array(z.string().uuid()).min(1).max(50),
});

export const reorderExperiences = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => reorderExpSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await supabase
        .from("experiences")
        .update({ display_order: -1000 - i })
        .eq("id", data.ordered_ids[i]);
      if (error) throw new Error(error.message);
    }
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await supabase
        .from("experiences")
        .update({ display_order: i, updated_at: new Date().toISOString() })
        .eq("id", data.ordered_ids[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

// ---------- Admin: junction tables ----------

const setCatExpSchema = z.object({
  category_id: z.string().min(1).max(100),
  experience_ids: z.array(z.string().uuid()).max(50),
});

export const setCategoryExperiences = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => setCatExpSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { error: delErr } = await supabase
      .from("category_experiences")
      .delete()
      .eq("category_id", data.category_id);
    if (delErr) throw new Error(delErr.message);
    if (data.experience_ids.length > 0) {
      const { error } = await supabase
        .from("category_experiences")
        .insert(data.experience_ids.map((eid) => ({ category_id: data.category_id, experience_id: eid })));
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

const setSlideExpSchema = z.object({
  slide_id: z.string().uuid(),
  experience_ids: z.array(z.string().uuid()).max(50),
});

export const setSlideExperiences = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => setSlideExpSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { error: delErr } = await supabase
      .from("slide_experiences")
      .delete()
      .eq("slide_id", data.slide_id);
    if (delErr) throw new Error(delErr.message);
    if (data.experience_ids.length > 0) {
      const { error } = await supabase
        .from("slide_experiences")
        .insert(data.experience_ids.map((eid) => ({ slide_id: data.slide_id, experience_id: eid })));
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

// ---------- Admin: overrides ----------

const upsertOverrideSchema = z.object({
  slide_id: z.string().uuid(),
  experience_id: z.string().uuid(),
  kicker: z.string().max(200).nullable().optional(),
  title: z.string().max(500).nullable().optional(),
  caption: z.string().max(1000).nullable().optional(),
  caption_long: z.string().max(4000).nullable().optional(),
  media_url: z.string().url().max(2000).nullable().optional(),
  media_type: z.enum(["image", "video"]).nullable().optional(),
});

export const upsertSlideOverride = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => upsertOverrideSchema.parse(input))
  .handler(async ({ data, context }) => {
    const row: {
      slide_id: string;
      experience_id: string;
      updated_at: string;
      kicker?: string | null;
      title?: string | null;
      caption?: string | null;
      caption_long?: string | null;
      media_url?: string | null;
      media_type?: string | null;
    } = {
      slide_id: data.slide_id,
      experience_id: data.experience_id,
      updated_at: new Date().toISOString(),
    };
    if (data.kicker !== undefined) row.kicker = data.kicker;
    if (data.title !== undefined) row.title = data.title;
    if (data.caption !== undefined) row.caption = data.caption;
    if (data.caption_long !== undefined) row.caption_long = data.caption_long;
    if (data.media_url !== undefined) row.media_url = data.media_url;
    if (data.media_type !== undefined) row.media_type = data.media_type;
    const { error } = await context.supabase
      .from("slide_overrides")
      .upsert(row, { onConflict: "slide_id,experience_id" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });


const deleteOverrideSchema = z.object({
  slide_id: z.string().uuid(),
  experience_id: z.string().uuid(),
});

export const deleteSlideOverride = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => deleteOverrideSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("slide_overrides")
      .delete()
      .eq("slide_id", data.slide_id)
      .eq("experience_id", data.experience_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
